// Live weather for the four cities that matter most to GB4K:
// Karakoto (児島唐琴, my house), Lyon (France), Beirut (Rotary club),
// Kampala (3Causes). Uses Open-Meteo (no API key).

export type CityKey = 'karakoto' | 'lyon' | 'beirut' | 'kampala';

export interface CityDef {
  key: CityKey;
  label: string;         // Display label for the badge / terminal
  labelLocal?: string;   // Native script label if it differs
  lat: number;
  lon: number;
  timezone: string;
  flag: string;
}

export const CITIES: Record<CityKey, CityDef> = {
  karakoto: {
    key: 'karakoto',
    label: 'Karakoto',
    labelLocal: '唐琴',
    lat: 34.488, lon: 133.832,
    timezone: 'Asia/Tokyo',
    flag: '🇯🇵',
  },
  lyon: {
    key: 'lyon',
    label: 'Lyon',
    lat: 45.7640, lon: 4.8357,
    timezone: 'Europe/Paris',
    flag: '🇫🇷',
  },
  beirut: {
    key: 'beirut',
    label: 'Beirut',
    labelLocal: 'بيروت',
    lat: 33.8938, lon: 35.5018,
    timezone: 'Asia/Beirut',
    flag: '🇱🇧',
  },
  kampala: {
    key: 'kampala',
    label: 'Kampala',
    lat: 0.3476, lon: 32.5825,
    timezone: 'Africa/Kampala',
    flag: '🇺🇬',
  },
};

export const CITY_KEYS: CityKey[] = ['karakoto', 'lyon', 'beirut', 'kampala'];

export interface CityWeather {
  cityKey: CityKey;
  temperatureC: number;
  windKph: number;
  weatherCode: number;
  isDay: boolean;
  fetchedAt: number;
}

interface CacheEntry { data: CityWeather; cachedAt: number; }

const CACHE_PREFIX = 'gb4k_weather_v2_';
const CACHE_TTL_MS = 15 * 60 * 1000;

// WMO weather codes → label + emoji + Japanese
const CODE_TABLE: Record<number, { label: string; jp: string; emoji: string }> = {
  0:  { label: 'Clear',                 jp: '快晴',       emoji: '☀️' },
  1:  { label: 'Mainly clear',          jp: '晴れ',       emoji: '🌤' },
  2:  { label: 'Partly cloudy',         jp: '一部曇り',   emoji: '⛅' },
  3:  { label: 'Overcast',              jp: '曇り',       emoji: '☁️' },
  45: { label: 'Fog',                   jp: '霧',         emoji: '🌫' },
  48: { label: 'Rime fog',              jp: '霧氷',       emoji: '🌫' },
  51: { label: 'Light drizzle',         jp: '小雨',       emoji: '🌦' },
  53: { label: 'Drizzle',               jp: '雨',         emoji: '🌦' },
  55: { label: 'Heavy drizzle',         jp: '強い小雨',   emoji: '🌧' },
  61: { label: 'Light rain',            jp: '弱い雨',     emoji: '🌧' },
  63: { label: 'Rain',                  jp: '雨',         emoji: '🌧' },
  65: { label: 'Heavy rain',            jp: '大雨',       emoji: '🌧' },
  71: { label: 'Light snow',            jp: '小雪',       emoji: '🌨' },
  73: { label: 'Snow',                  jp: '雪',         emoji: '🌨' },
  75: { label: 'Heavy snow',            jp: '大雪',       emoji: '❄️' },
  77: { label: 'Snow grains',           jp: '霰',         emoji: '❄️' },
  80: { label: 'Rain showers',          jp: 'にわか雨',   emoji: '🌦' },
  81: { label: 'Rain showers',          jp: 'にわか雨',   emoji: '🌧' },
  82: { label: 'Violent showers',       jp: '激しい雨',   emoji: '⛈' },
  85: { label: 'Snow showers',          jp: 'にわか雪',   emoji: '🌨' },
  86: { label: 'Heavy snow showers',    jp: '激しい雪',   emoji: '❄️' },
  95: { label: 'Thunderstorm',          jp: '雷雨',       emoji: '⛈' },
  96: { label: 'Thunderstorm + hail',   jp: '雷と雹',     emoji: '⛈' },
  99: { label: 'Severe thunderstorm',   jp: '激しい雷雨', emoji: '⛈' },
};

export function describeCode(code: number) {
  return CODE_TABLE[code] ?? { label: `Code ${code}`, jp: '不明', emoji: '❓' };
}

function cacheKeyFor(cityKey: CityKey): string {
  return CACHE_PREFIX + cityKey;
}

function loadCache(cityKey: CityKey): CacheEntry | null {
  try {
    const raw = localStorage.getItem(cacheKeyFor(cityKey));
    if (!raw) return null;
    const c = JSON.parse(raw) as CacheEntry;
    if (!c || typeof c.cachedAt !== 'number') return null;
    return c;
  } catch {
    return null;
  }
}

function saveCache(cityKey: CityKey, data: CityWeather): void {
  try {
    localStorage.setItem(cacheKeyFor(cityKey), JSON.stringify({ data, cachedAt: Date.now() }));
  } catch {
    // ignore
  }
}

export function getCachedWeatherFor(cityKey: CityKey): CityWeather | null {
  return loadCache(cityKey)?.data ?? null;
}

export async function fetchWeatherFor(cityKey: CityKey, force = false): Promise<CityWeather | null> {
  const city = CITIES[cityKey];
  if (!city) return null;
  const cached = loadCache(cityKey);
  if (!force && cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.data;
  }
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${city.lat}&longitude=${city.lon}` +
      `&current=temperature_2m,wind_speed_10m,weather_code,is_day` +
      `&timezone=${encodeURIComponent(city.timezone)}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const c = json.current;
    if (!c) throw new Error('No current block');
    const data: CityWeather = {
      cityKey,
      temperatureC: c.temperature_2m,
      windKph: c.wind_speed_10m,
      weatherCode: c.weather_code,
      isDay: c.is_day === 1,
      fetchedAt: Date.now(),
    };
    saveCache(cityKey, data);
    return data;
  } catch (err) {
    console.warn(`[Weather] fetch failed for ${cityKey}:`, err);
    return cached?.data ?? null;
  }
}

export async function fetchAllCities(force = false): Promise<Record<CityKey, CityWeather | null>> {
  const entries = await Promise.all(
    CITY_KEYS.map(async (k) => [k, await fetchWeatherFor(k, force)] as const)
  );
  return Object.fromEntries(entries) as Record<CityKey, CityWeather | null>;
}

// ===== Backwards-compatible API for code that still references the old
// Karakoto-only helpers. Deprecated — prefer fetchWeatherFor('karakoto'). =====

export interface KarakotoWeather extends CityWeather {}

export async function fetchKarakotoWeather(force = false): Promise<KarakotoWeather | null> {
  return fetchWeatherFor('karakoto', force);
}

export function getCachedWeather(): KarakotoWeather | null {
  return getCachedWeatherFor('karakoto');
}
